import { t } from "@hakili/i18n";

/**
 * `ASK-WIDE` (D-1028-4, contract part 1, lane AD's read at `7e1f7b26`): **the strip « Pièces du dossier » lists what the dossier has actually received, from
 * served data**: the document evidence of the analysis' points carries the pièce's code, its name from the door and its receipt time; a pièce with no receipt
 * time is not on the strip. The codes are the catalogue's five (05-data-model §3.5); a code outside them (the prototype's « proforma »)
 * would meet a `404` at the door and is not drawn. The expertise report is not a pièce: it has its own chip and its own door.
 */
const OF_RECORD: readonly { readonly code: string; readonly words: Parameters<typeof t>[0] }[] = [
  { code: "pieceIdentite", words: "bank.conversation.pieceIdentite" },
  { code: "bulletinsSalaire", words: "bank.conversation.pieceBulletins" },
  { code: "relevesBancaires", words: "bank.conversation.pieceReleves" },
  { code: "attestationTravail", words: "bank.conversation.pieceAttestation" },
  { code: "factureProforma", words: "bank.conversation.pieceProforma" },
];

/**
 * The part of an analysis point the strip reads, written here so this file imports no screen (4.6.4): `AnalysisPointView` is
 * assignable to it.
 */
export interface EvidencedPoint {
  readonly evidence:
    | {
        readonly kind: "document";
        readonly documentCode: string;
        readonly documentLabel: string | null;
        readonly receivedAt: string | null;
      }
    | { readonly kind: "figures" }
    | null;
}

export interface ReceivedDocument {
  readonly code: string;
  readonly label: string;
}

const capital = (words: string): string => words.charAt(0).toUpperCase() + words.slice(1);

export function receivedDocuments(
  analysis: { readonly points: readonly EvidencedPoint[] } | null,
): readonly ReceivedDocument[] {
  if (analysis === null) return [];
  const received = new Map<string, string | null>();
  for (const point of analysis.points) {
    const evidence = point.evidence;
    if (evidence?.kind !== "document" || evidence.receivedAt === null) continue;
    if (!received.has(evidence.documentCode) || received.get(evidence.documentCode) === null) {
      received.set(evidence.documentCode, evidence.documentLabel);
    }
  }
  return OF_RECORD.flatMap((one) => {
    if (!received.has(one.code)) return [];
    const named = received.get(one.code) ?? null;
    return [{ code: one.code, label: named === null || named === "" ? t(one.words) : capital(named) }];
  });
}

const rec = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;

const fileFrom = (value: unknown): { fileName: string; mimeType: string; url: string } | null => {
  const one = rec(value);
  const url = one?.["url"];
  if (one === null || typeof url !== "string") return null;
  const name = one["fileName"];
  const mime = one["mimeType"];
  return {
    fileName: typeof name === "string" ? name : "",
    mimeType: typeof mime === "string" ? mime : "",
    url,
  };
};

/**
 * The expertise report as the viewer opens it (`GET /api/v1/bank/files/{id}/rapport`, asked on every opening): the cabinet's paper, then
 * Hakili's reading sheet, each as a signed link. `null` where no report stands or it carries neither.
 */
export function reportFilesFrom(
  answer: unknown,
): readonly { fileName: string; mimeType: string; url: string }[] | null {
  const report = rec(rec(answer)?.["report"]);
  if (report === null) return null;
  const files = [fileFrom(report["paper"]), fileFrom(report["sheet"])].flatMap((one) =>
    one === null ? [] : [one],
  );
  return files.length === 0 ? null : files;
}
